import type { Game, TeamRecord } from './types.js';

export const BRAND = {
  name: 'FanVote25',
  tagline: 'College Football. Ranked by the Fans.',
  phrases: [
    'Your Ballot. Your Rankings.',
    'You watched the games. Now rank them.',
    'The Fans Decide.',
    'Build Your Ballot.',
    'Every Ballot Counts.',
    'Saturdays Decide. Fans Rank.',
    'Who belongs in the Top 25?',
    'Make Your Case.',
    'The National Fan Poll.',
  ],
} as const;

export function formatRecord(record?: Pick<TeamRecord, 'wins' | 'losses'> | null): string {
  if (!record) return '0-0';
  return `${record.wins}-${record.losses}`;
}

export function formatConferenceRecord(
  record?: Pick<TeamRecord, 'conferenceWins' | 'conferenceLosses'> | null,
): string {
  if (!record) return '0-0';
  return `${record.conferenceWins}-${record.conferenceLosses}`;
}

export function formatPoints(points: number): string {
  return points.toLocaleString('en-US');
}

export function formatRank(rank: number | null | undefined): string {
  return rank == null ? 'NR' : `#${rank}`;
}

export function ordinal(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

const ET_FORMAT: Intl.DateTimeFormatOptions = {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

export function formatKickoff(iso: string): string {
  return `${new Date(iso).toLocaleString('en-US', ET_FORMAT)} ET`;
}

export function formatKickoffShort(iso: string): string {
  const date = new Date(iso);
  const day = date.toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short' }).toUpperCase();
  const time = date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${day} ${time} ET`;
}

export function formatDeadline(iso: string): string {
  const date = new Date(iso);
  const weekday = date.toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'long' });
  const month = date.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short' });
  const day = date.toLocaleString('en-US', { timeZone: 'America/New_York', day: 'numeric' });
  const time = date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${weekday}, ${month} ${day} at ${time} ET`;
}

export function relativeTime(iso: string, now: Date = new Date()): string {
  const diff = now.getTime() - Date.parse(iso);
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function gameStatusLabel(game: Pick<Game, 'status' | 'period' | 'clock' | 'startTime'>): string {
  switch (game.status) {
    case 'final':
      return game.period && game.period !== 'FINAL' ? `Final/${game.period}` : 'Final';
    case 'in-progress':
      return [game.period, game.clock].filter(Boolean).join(' ') || 'Live';
    case 'postponed':
      return 'Postponed';
    case 'canceled':
      return 'Canceled';
    default:
      return formatKickoffShort(game.startTime);
  }
}

export function displayName(firstName: string, lastName: string): string {
  const initial = lastName ? `${lastName.charAt(0).toUpperCase()}.` : '';
  return [firstName, initial].filter(Boolean).join(' ');
}

export function initialsFor(value: string): string {
  const parts = value.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

/**
 * Picks readable foreground text for a school-colored chip.
 * Uses the WCAG relative-luminance formula so the ballot stays legible for
 * every one of the 136 color pairs in the team database.
 */
export function readableTextColor(hex: string): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return '#FFFFFF';
  const channel = (offset: number) => {
    const value = parseInt(normalized.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.45 ? '#0B1220' : '#FFFFFF';
}

/** Deterministic avatar color so a voter looks the same everywhere. */
export function avatarColorFor(seed: string): string {
  const palette = [
    '#E63946',
    '#F77F00',
    '#FCBF49',
    '#06D6A0',
    '#118AB2',
    '#7B2CBF',
    '#EF476F',
    '#2A9D8F',
    '#3A86FF',
    '#FB5607',
  ];
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return palette[hash % palette.length];
}
